/**
 * ExchangeDraftAssist — Slice 3 "draft, never throw" affordances.
 *
 * Two gated components for the Exchange (Catchball):
 *   - LoopIntentDraftAssist       Request composer: editable Loop Intent
 *                                 fields + deterministic ambiguity checklist
 *                                 (always shown when the module is on) + an
 *                                 optional "Draft with AI" fill.
 *   - ProposalAnnotationDraftAssist  Proposal Formation drawer: "Draft with
 *                                 AI" for rationale/tradeoffs/assumptions.
 *
 * Both write ONLY into local form state via callbacks — the ball-holder
 * reviews, edits, and sends through the existing buttons. Zero writes, zero
 * RPCs here (Authority & Refusal Matrix §3). Gate: useModuleGate
 * ('exchange_drafting') per build contract §2.7, plus the capability hook
 * for the LLM affordance.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { useModuleGate } from '../../../lib/moduleGate';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import { lintLoopIntent } from '../../../lib/ai/loopIntentLint';
import { buildLoopPacket, type LoopPacket } from '../../../lib/ai/contextPacket';
import {
  draftLoopIntent,
  draftProposalAnnotation,
  type ExchangeDraftResult,
} from '../../../lib/llmExchangeDraftService';
import {
  getBallPossession,
  getBallStance,
  type CatchballLoop,
} from '../../../types/prioritization/catchballLoop';

const DRAFT_NEVER_THROW_COPY =
  'Drafts fill this form for you to review and edit — sending stays with you. The AI never moves the ball.';

const LINT_FIELD_LABELS: Record<string, string> = {
  strategicContext: 'Strategic context',
  asks: 'Asks',
  constraints: 'Constraints',
  guardrails: 'Guardrails',
  responseGuidance: 'Response guidance',
};

function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildLoopPacketFromLoop(
  loop: CatchballLoop,
  intentFields: LoopIntentDraftFields | null,
): LoopPacket {
  return buildLoopPacket({
    loop: { id: loop.id, state: String(loop.state), cycleId: loop.cycleId },
    ball: {
      holder: `${getBallPossession(loop.state)} tier`,
      stance: getBallStance(loop.state),
    },
    intentFields,
    legitimacyMarking: loop.intent?.legitimacyMarking ?? null,
    iterationCount: loop.loopNumber,
    clarificationsCount: loop.intent?.clarifications?.length ?? 0,
    // Blockers and readiness gates are not loaded on these surfaces —
    // buildLoopPacket names them as omissions.
  });
}

// ── Loop Intent (Request composer) ──────────────────────────────────────────

export interface LoopIntentDraftFields {
  strategicContext: string;
  asks: string[];
  constraints: string[];
  guardrails: string[];
  responseGuidance: string;
}

export function LoopIntentDraftAssist({
  loop,
  fields,
  onFieldsChange,
}: {
  loop: CatchballLoop | null;
  fields: LoopIntentDraftFields;
  onFieldsChange: (next: Partial<LoopIntentDraftFields>) => void;
}) {
  const moduleOn = useModuleGate('exchange_drafting');
  const aiOn = useAiCapability('exchange_drafting');

  // List fields edit as one-per-line text; raw text is local so typing a
  // newline is not eaten by the array round-trip.
  const [listText, setListText] = useState(() => ({
    asks: fields.asks.join('\n'),
    constraints: fields.constraints.join('\n'),
    guardrails: fields.guardrails.join('\n'),
  }));
  const [userNotes, setUserNotes] = useState('');
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftNotes, setDraftNotes] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  const loopId = loop?.id ?? null;
  useEffect(() => {
    // Re-seed the line editors when switching loops (same idiom as the
    // composer's own hydration guard).
    setListText({
      asks: fields.asks.join('\n'),
      constraints: fields.constraints.join('\n'),
      guardrails: fields.guardrails.join('\n'),
    });
    setDraftNotes(null);
    setDraftError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loopId]);

  const findings = useMemo(
    () =>
      lintLoopIntent({
        strategicContext: fields.strategicContext,
        asks: fields.asks,
        constraints: fields.constraints,
        guardrails: fields.guardrails,
        responseGuidance: fields.responseGuidance,
        legitimacyMarking: loop?.intent?.legitimacyMarking ?? null,
      }),
    [fields, loop?.intent?.legitimacyMarking],
  );

  if (!moduleOn) return null;

  const setListField = (
    key: 'asks' | 'constraints' | 'guardrails',
    text: string,
  ) => {
    setListText((current) => ({ ...current, [key]: text }));
    onFieldsChange({ [key]: splitLines(text) });
  };

  const applyDrafts = (result: ExchangeDraftResult) => {
    const next: Partial<LoopIntentDraftFields> = {};
    if (result.drafts.strategicContext) next.strategicContext = result.drafts.strategicContext;
    if (result.drafts.responseGuidance) next.responseGuidance = result.drafts.responseGuidance;
    const textUpdates: Partial<typeof listText> = {};
    for (const key of ['asks', 'constraints', 'guardrails'] as const) {
      const draft = result.drafts[key];
      if (draft) {
        next[key] = splitLines(draft);
        textUpdates[key] = splitLines(draft).join('\n');
      }
    }
    if (Object.keys(textUpdates).length > 0) {
      setListText((current) => ({ ...current, ...textUpdates }));
    }
    onFieldsChange(next);
    setDraftNotes(result.notes || 'Draft applied — review and edit before sending.');
  };

  const handleDraft = async () => {
    if (!loop) return;
    setIsDrafting(true);
    setDraftError(null);
    setDraftNotes(null);
    try {
      const packet = buildLoopPacketFromLoop(loop, fields);
      const result = await draftLoopIntent({
        packet,
        userNotes: userNotes.trim() || undefined,
      });
      if (!result) {
        setDraftError('No draft came back — the AI assist may be off or unavailable. Your text is unchanged.');
        return;
      }
      applyDrafts(result);
    } finally {
      setIsDrafting(false);
    }
  };

  return (
    <section className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Loop intent</h2>
          <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            The request the receiving tier will interpret. {DRAFT_NEVER_THROW_COPY}
          </p>
        </div>
        {aiOn && (
          <button
            type="button"
            onClick={() => void handleDraft()}
            disabled={!loop || isDrafting}
            className="inline-flex items-center gap-2 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--slate-dim)] disabled:opacity-60 dark:border-[var(--slate)]/40 dark:bg-[var(--slate)]/10"
          >
            {isDrafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {isDrafting ? 'Drafting…' : 'Draft with AI'}
          </button>
        )}
      </div>

      {aiOn && (
        <input
          value={userNotes}
          onChange={(event) => setUserNotes(event.target.value)}
          placeholder="Anything the draft should reflect (optional) — e.g. budget at 70%, no new hires"
          className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
        />
      )}

      {draftError && (
        <p className="bg-[var(--mustard-dim)] px-3 py-2 text-sm text-[var(--mustard)]">{draftError}</p>
      )}
      {draftNotes && (
        <p className="bg-[var(--slate-dim)] px-3 py-2 text-sm text-[var(--text-sec)] dark:bg-[var(--slate)]/10">
          AI draft applied. {draftNotes}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Strategic context</label>
          <textarea
            value={fields.strategicContext}
            onChange={(event) => onFieldsChange({ strategicContext: event.target.value })}
            rows={3}
            placeholder="Why this decision matters now (vision, strategy, no-fail goals)."
            className="mt-2 w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Asks <span className="font-normal text-[var(--text-ter)]">(one per line)</span></label>
          <textarea
            value={listText.asks}
            onChange={(event) => setListField('asks', event.target.value)}
            rows={3}
            placeholder="Explicit questions or deliverables requested."
            className="mt-2 w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Constraints <span className="font-normal text-[var(--text-ter)]">(one per line — constraints block)</span></label>
          <textarea
            value={listText.constraints}
            onChange={(event) => setListField('constraints', event.target.value)}
            rows={3}
            placeholder="Hard limits: budget, timeline, headcount."
            className="mt-2 w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Guardrails <span className="font-normal text-[var(--text-ter)]">(one per line — guardrails guide)</span></label>
          <textarea
            value={listText.guardrails}
            onChange={(event) => setListField('guardrails', event.target.value)}
            rows={3}
            placeholder="Lines that must not be crossed."
            className="mt-2 w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
          />
        </div>
      </div>

      {/* Deterministic ambiguity checklist — shown even when the LLM is off. */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)] p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Ambiguity check
        </h3>
        {findings.length === 0 ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            No ambiguity flags on this draft.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {findings.map((finding, index) => (
              <li key={`${finding.field}-${index}`} className="flex items-start gap-2 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <span className="font-medium">{LINT_FIELD_LABELS[finding.field] ?? finding.field}:</span>{' '}
                  {finding.message}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Deterministic check against the interpretation_unclear blocker — no AI involved.
        </p>
      </div>
    </section>
  );
}

// ── Proposal annotation (Proposal Formation drawer) ─────────────────────────

export function ProposalAnnotationDraftAssist({
  loop,
  loopId,
  cycleId,
  loopNumber,
  sessionResultSummary,
  onApply,
}: {
  /** The loop when the caller's store has it; falls back to ids with named omissions. */
  loop: CatchballLoop | null;
  loopId: string;
  cycleId: string;
  loopNumber?: number;
  /** Deterministic provenance summary of the session result being packaged. */
  sessionResultSummary: string;
  onApply: (drafts: { rationale?: string; scope?: string; tradeoffs?: string; assumptions?: string }, notes: string) => void;
}) {
  const moduleOn = useModuleGate('exchange_drafting');
  const aiOn = useAiCapability('exchange_drafting');
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState<string | null>(null);

  if (!moduleOn || !aiOn) return null;

  const handleDraft = async () => {
    setIsDrafting(true);
    setDraftError(null);
    setDraftNotes(null);
    try {
      const packet = loop
        ? buildLoopPacketFromLoop(loop, null)
        : buildLoopPacket({
            loop: { id: loopId, state: 'unknown', cycleId },
            iterationCount: loopNumber ?? null,
          });
      const result = await draftProposalAnnotation({ packet, sessionResultSummary });
      if (!result) {
        setDraftError('No draft came back — the AI assist may be off or unavailable. Your text is unchanged.');
        return;
      }
      onApply(
        {
          rationale: result.drafts.rationale,
          scope: result.drafts.scope,
          tradeoffs: result.drafts.tradeoffs,
          assumptions: result.drafts.assumptions,
        },
        result.notes,
      );
      setDraftNotes(result.notes || 'Draft applied — review and edit before submitting.');
    } finally {
      setIsDrafting(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => void handleDraft()}
        disabled={isDrafting}
        className="inline-flex items-center gap-2 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--slate-dim)] disabled:opacity-60 transition-colors"
      >
        {isDrafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        {isDrafting ? 'Drafting…' : 'Draft with AI'}
      </button>
      <p className="text-xs text-[var(--text-ter)]">{DRAFT_NEVER_THROW_COPY}</p>
      {draftError && <p className="bg-[var(--mustard-dim)] px-3 py-2 text-xs text-[var(--mustard)]">{draftError}</p>}
      {draftNotes && (
        <p className="bg-[var(--slate-dim)] px-3 py-2 text-xs text-[var(--text-sec)]">AI draft applied. {draftNotes}</p>
      )}
    </div>
  );
}
